import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, AlertCircle, UsersRound, Sparkles } from "lucide-react";

/**
 * ShareAccept — landing for /share/accept/:token
 * Lets an invitee preview an invite and (after login/signup) claim access.
 */
export default function ShareAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    api
      .get(`/memberships/share/preview/${token}`)
      .then((r) => setPreview(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Invite not found."));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.post(`/memberships/share/accept/${token}`);
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not claim this invite.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white min-h-[70vh]" data-testid="share-accept-page">
      <section
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, #FAF7F1, #FFFFFF)" }}
      >
        <div className="max-w-2xl mx-auto px-6 py-20 lg:py-24 text-center">
          {done ? (
            <>
              <CheckCircle2 size={48} className="mx-auto text-[var(--ff-primary)]" />
              <h1 className="mt-6 font-display text-4xl text-[var(--ff-text-primary)]">
                You're in.
              </h1>
              <p className="mt-4 text-[var(--ff-text-secondary)] leading-relaxed">
                Premier membership access is now linked to your account. Shop
                limited drops, curated boxes, and member perks while the host's
                membership stays active.
              </p>
              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <button
                  type="button"
                  onClick={() => navigate("/drops")}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="share-shop-drops"
                >
                  <Sparkles size={14} /> See current drops
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/account")}
                  className="btn-secondary"
                  data-testid="share-go-account"
                >
                  Go to account
                </button>
              </div>
            </>
          ) : err ? (
            <>
              <AlertCircle size={40} className="mx-auto text-rose-700" />
              <h1 className="mt-5 font-display text-3xl">Invite unavailable</h1>
              <p className="mt-3 text-[var(--ff-text-secondary)]" data-testid="share-accept-err">
                {err}
              </p>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="mt-6 btn-secondary"
              >
                Back to home
              </button>
            </>
          ) : preview ? (
            preview.valid ? (
              <>
                <UsersRound size={40} className="mx-auto text-[var(--ff-secondary)]" />
                <p className="overline mt-4 text-[var(--ff-secondary)]">Premier membership</p>
                <h1 className="mt-3 font-display text-4xl text-[var(--ff-text-primary)]">
                  {preview.host_name || "A Farm To You member"} invited you.
                </h1>
                <p className="mt-4 text-[var(--ff-text-secondary)] leading-relaxed">
                  Claim your shared Premier membership access — 72-hour early
                  access to limited drops, curated boxes, and free shipping on
                  every order, while the host's membership is active.
                </p>
                {preview.invite_email && (
                  <p className="mt-3 text-xs text-[var(--ff-text-muted)]">
                    Invite issued to {preview.invite_email}
                  </p>
                )}
                {!user ? (
                  <>
                    <p className="mt-8 text-sm text-[var(--ff-text-secondary)]">
                      Create or sign in to your account to claim.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-3 justify-center">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/auth?from=/share/accept/${token}&mode=signup`)
                        }
                        className="btn-primary"
                        data-testid="share-signup"
                      >
                        Create account
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/auth?from=/share/accept/${token}&mode=signin`)
                        }
                        className="btn-secondary"
                        data-testid="share-signin"
                      >
                        Sign in
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={accept}
                    disabled={busy}
                    className="mt-8 btn-primary inline-flex items-center gap-2"
                    data-testid="share-accept-btn"
                  >
                    {busy ? "Linking…" : "Claim my access"}
                  </button>
                )}
              </>
            ) : (
              <>
                <AlertCircle size={40} className="mx-auto text-rose-700" />
                <h1 className="mt-5 font-display text-3xl">Invite unavailable</h1>
                <p className="mt-3 text-[var(--ff-text-secondary)]">
                  {preview.message}
                </p>
              </>
            )
          ) : (
            <p className="text-[var(--ff-text-muted)]">Loading invite…</p>
          )}
        </div>
      </section>
    </div>
  );
}
